import { Role } from "../../auth/types/authType";
import { RoleTypes } from "../../auth/types/roleTypes";
import { isPrivilegedTransaction } from "../../../utils/authUtils";
import { isNoEmbassyCountry } from "../processConstants";
import { Employee, EmployeeStatus, MedicalStatus } from "../types/processTypes";

export type EmployeeStatusTabValue =
  | "pendingInfo"
  | "available"
  | "selected"
  | "contracted"
  | "embassyProcess"
  | "lmisProcess"
  | "ticketing"
  | "flightBooked"
  | "departed"
  | "discontinuedStatuses"
  | "stoppedStatuses";

const EMPLOYEE_TAB_PROCESS_PRIVILEGES: Record<
  EmployeeStatusTabValue,
  RoleTypes[]
> = {
  pendingInfo: [
    RoleTypes.RegisterCandidate,
    RoleTypes.SelectCandidates,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  available: [
    RoleTypes.SelectCandidates,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  selected: [
    RoleTypes.SelectCandidates,
    RoleTypes.ContractEmployees,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  contracted: [
    RoleTypes.SelectCandidates,
    RoleTypes.ContractEmployees,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  embassyProcess: [
    RoleTypes.IssueEmployeeVisa,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  lmisProcess: [
    RoleTypes.IssueEmployeeLabour,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  ticketing: [
    RoleTypes.BookEmployeeTicket,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  flightBooked: [
    RoleTypes.BookEmployeeTicket,
    RoleTypes.MarkEmployeeDeparted,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  departed: [
    RoleTypes.MarkEmployeeDeparted,
    RoleTypes.ChangeEmployeeDisposition,
  ],
  discontinuedStatuses: [RoleTypes.DiscontinueProcess],
  stoppedStatuses: [RoleTypes.StopProcess],
};

export const MEDICAL_ACTION_TABS: EmployeeStatusTabValue[] = [
  "pendingInfo",
  "available",
  "selected",
  "contracted",
  "embassyProcess",
  "lmisProcess",
];

const TAB_RESOLUTION_ORDER: EmployeeStatusTabValue[] = [
  "stoppedStatuses",
  "discontinuedStatuses",
  "departed",
  "flightBooked",
  "ticketing",
  "lmisProcess",
  "embassyProcess",
  "contracted",
  "selected",
  "available",
  "pendingInfo",
];

function employeeMatchesContractedPrepTab(emp: Employee): boolean {
  const s = emp.status as EmployeeStatus | undefined;
  if (
    s !== EmployeeStatus.contracted &&
    s !== EmployeeStatus.visa_issued &&
    s !== EmployeeStatus.labour_issued
  ) {
    return false;
  }
  return emp.medicalStatus !== MedicalStatus.fit || emp.cocCompleted !== true;
}

export function employeeMatchesActiveTab(
  emp: Employee,
  tabValue: EmployeeStatusTabValue,
): boolean {
  const s = emp.status as EmployeeStatus | undefined;
  if (tabValue === "contracted") {
    return employeeMatchesContractedPrepTab(emp);
  }
  if (tabValue === "embassyProcess") {
    const okStatus =
      s === EmployeeStatus.contracted || s === EmployeeStatus.labour_issued;
    return (
      okStatus &&
      emp.visaIssued !== true &&
      emp.medicalStatus === MedicalStatus.fit &&
      !isNoEmbassyCountry(emp.additionalData?.appliedCountry)
    );
  }
  if (tabValue === "lmisProcess") {
    const okStatus =
      s === EmployeeStatus.contracted || s === EmployeeStatus.visa_issued;
    return (
      okStatus &&
      emp.labourIssued !== true &&
      Boolean(emp.cocCompleted) &&
      emp.medicalStatus === MedicalStatus.fit
    );
  }
  if (tabValue === "ticketing") {
    const okStatus =
      s === EmployeeStatus.visa_issued || s === EmployeeStatus.labour_issued;
    return okStatus && emp.visaIssued === true && emp.labourIssued === true;
  }
  if (tabValue === "discontinuedStatuses") {
    return s === EmployeeStatus.discontinued;
  }
  if (tabValue === "stoppedStatuses") {
    return s === EmployeeStatus.stopped;
  }
  if (tabValue === "pendingInfo") {
    return s === EmployeeStatus.pending;
  }
  if (tabValue === "available") {
    return s === EmployeeStatus.new;
  }
  if (tabValue === "selected") {
    return s === EmployeeStatus.selected;
  }
  if (tabValue === "flightBooked") {
    return s === EmployeeStatus.flight_booked;
  }
  if (tabValue === "departed") {
    return s === EmployeeStatus.departed;
  }
  return false;
}

export function resolveEmployeeStatusTab(
  emp: Employee,
): EmployeeStatusTabValue {
  for (const tab of TAB_RESOLUTION_ORDER) {
    if (employeeMatchesActiveTab(emp, tab)) return tab;
  }
  return "available";
}

export function canOpenProcessStagesForTab(
  roles: Role[] | undefined,
  tab: EmployeeStatusTabValue,
): boolean {
  const r = roles ?? [];
  if (isPrivilegedTransaction(r, RoleTypes.ChangeEmployeeStatus)) {
    return true;
  }
  const privileges = EMPLOYEE_TAB_PROCESS_PRIVILEGES[tab];
  return privileges.some((p) => isPrivilegedTransaction(r, p));
}

export const VIEW_CV_STATUSES = new Set<EmployeeStatus>([
  EmployeeStatus.pending,
  EmployeeStatus.new,
  EmployeeStatus.selected,
]);

export function shouldShowViewCvAction(
  employee: Employee,
  roles: Role[] | undefined,
): boolean {
  const status = employee.status as EmployeeStatus;
  return (
    isPrivilegedTransaction(roles, "Employees.View") &&
    VIEW_CV_STATUSES.has(status)
  );
}

export function shouldShowDeselectAction(
  employee: Employee,
  roles: Role[] | undefined,
  activeStatusTab?: EmployeeStatusTabValue,
): boolean {
  const tab = activeStatusTab ?? resolveEmployeeStatusTab(employee);
  const status = employee.status as EmployeeStatus;
  return (
    (tab === "available" ||
      tab === "selected" ||
      tab === "contracted") &&
    (status === EmployeeStatus.selected ||
      status === EmployeeStatus.contracted) &&
    isPrivilegedTransaction(roles, "ProcessControl.Deselect")
  );
}

export function shouldShowProcessStagesAction(
  employee: Employee,
  roles: Role[] | undefined,
  activeStatusTab?: EmployeeStatusTabValue,
): boolean {
  const tab = activeStatusTab ?? resolveEmployeeStatusTab(employee);
  return (
    tab !== "available" &&
    tab !== "pendingInfo" &&
    tab !== "selected" &&
    tab !== "stoppedStatuses" &&
    canOpenProcessStagesForTab(roles, tab)
  );
}
